import { useState, useEffect, useCallback } from 'react'
import { api } from '../../lib/api.js'
import { demoPursuits } from './mockPursuits.js'

/**
 * Load one pursuit "line" (engine) from GET /api/pursuits?line=<line> — the
 * JWT proxy to sr-pursuit-tracker. Cursor-paginates all pages. `?demo=1` uses
 * bundled config-driven demo data.
 *
 * A falsy `line` is a real state, not an error: the Command Center's category
 * spaces hold this hook mounted while a non-pursuit (or not-yet-sourced) space
 * is selected. With no line there is nothing to ask for, so the hook idles —
 * it never fires a request for a line that doesn't exist.
 */
function isDemo() {
  if (typeof window === 'undefined') return false
  return new URLSearchParams(window.location.search).get('demo') === '1'
}

export function usePursuits(line) {
  const demo = isDemo()
  const [rows, setRows] = useState([])
  const [owners, setOwners] = useState({})
  const [config, setConfig] = useState(null)
  const [status, setStatus] = useState(line ? 'loading' : 'idle') // idle | loading | done | error
  const [error, setError] = useState('')

  const load = useCallback(
    async (signal) => {
      if (!line) {
        setStatus('idle'); setError(''); setConfig(null); setRows([])
        return
      }
      setStatus('loading'); setError(''); setConfig(null); setRows([])
      if (demo) {
        const d = demoPursuits(line)
        setRows(d.results); setOwners(d.owners); setConfig(d.config); setStatus('done')
        return
      }
      try {
        let after = null
        const acc = []
        do {
          const q = { line }
          if (after) q.after = after
          const d = await api.pursuits(q, { signal })
          if (signal?.aborted) return
          acc.push(...(d.results || []))
          if (d.owners) setOwners(d.owners)
          if (d.config) setConfig(d.config)
          setRows([...acc])
          after = d.after || null
        } while (after && !signal?.aborted)
        setStatus('done')
      } catch (err) {
        if (err?.name === 'AbortError' || signal?.aborted) return
        setError(err?.message || 'Could not load pursuits.')
        setStatus('error')
      }
    },
    [demo, line]
  )

  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
    return () => controller.abort()
  }, [load])

  const reload = useCallback(() => load(), [load])

  return { rows, owners, config, status, error, reload }
}
